import React from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle, css } from 'styled-components';
import { Box, Flex } from '@rebass/grid';
import { getTokenValue, SPACE, THEME_TOKENS } from '@amtrav/theme-system';
import { media, DynamicThemeProvider, ZINDEX } from '@amtrav/themes';
import { LoadingBar } from '@amtrav/view-components';
import Content from './Content';

export const ShadedRegion = styled(Box)`
  @media screen {
    background-color: ${getTokenValue(THEME_TOKENS.card.color.background)};
    border-bottom: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  }
`;

const PageHeader = styled.h2`
  font-weight: 600;
  font-size: 19px;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  line-height: 1;
  margin: 0;
  padding: 0;
`;

const MainContent = styled(Box)`
  max-width: ${({ contentMaxWidth }) => contentMaxWidth};
`;

const GlobalStyles = createGlobalStyle`
  html {
    background: ${getTokenValue(THEME_TOKENS.base.color.siteBackground)};
  }
`;

const SidebarContent = styled(Flex)`
  background-color: ${({ transparentSidebar }) =>
    transparentSidebar ? 'transparent' : getTokenValue(THEME_TOKENS.base.color.sidebarBackground)};
  justify-content: center;
  width: 100%;

  ${media.XLargeOnly`
    max-width: ${({ maxSidebarWidth }) => maxSidebarWidth}; 
    flex: 1 0 auto;
  `};
`;

const ContentPadding = styled(Box)`
  @media print {
    padding: 0;
  }
`;

const ContentChildren = styled(Flex)`
  ${({ hasSideBar }) =>
    hasSideBar &&
    css`
      min-height: calc(100vh - 78px);
    `};
  // this is for Safari & IE
  height: 100%;
`;

const LoaderRegion = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 5px;
  overflow: hidden;
  z-index: ${ZINDEX.OVERLAY};

  ${media.Large`
    left: 1px;
  `};
`;

const { theme } = document.querySelector('body').dataset;

const PageContent = ({
  pageHeader,
  pageHeaderAside,
  mainContent,
  sidebarContent,
  secondaryContent,
  fullWidth = false,
  pageHeaderSecondary,
  maxSidebarWidth = '405px',
  promoteSidebar,
  transparentSidebar,
  contentMaxWidth = '1325px',
  isPageLoading = false,
  pageLoadingDuration = 10000,
}) => (
  <>
    <DynamicThemeProvider themeName={theme || 'cava'}>
      <GlobalStyles />

      {pageHeader || pageHeaderSecondary || pageHeaderAside ? (
        <ShadedRegion>
          <Content fullWidth={fullWidth}>
            <ContentPadding
              pl={[SPACE.size12, SPACE.size12, SPACE.size12, SPACE.size32]}
              pr={[SPACE.size12, SPACE.size12, SPACE.size12, SPACE.size16]}
              py="14px"
              maxWidth={contentMaxWidth}>
              <Flex width="100%" justifyContent="space-between" alignItems="center">
                <Box width="100%">
                  <Flex justifyContent="space-between" alignItems="center">
                    {pageHeader ? <PageHeader>{pageHeader}</PageHeader> : null}
                    {pageHeaderAside ? <Box flex="0 0 auto">{pageHeaderAside}</Box> : null}
                  </Flex>
                  {pageHeaderSecondary || null}
                </Box>
              </Flex>
            </ContentPadding>
          </Content>
        </ShadedRegion>
      ) : null}
      {isPageLoading ? (
        <LoaderRegion>
          <Content fullWidth>
            <LoadingBar duration={pageLoadingDuration} />
          </Content>
        </LoaderRegion>
      ) : null}
      <Content fullWidth={fullWidth}>
        <ContentChildren
          hasSideBar={sidebarContent}
          flexDirection={sidebarContent ? ['column', 'column', 'column', 'column', 'row'] : 'column'}>
          <MainContent
            order={promoteSidebar ? [1, 1, 1, 1, 0] : null}
            flex="1 1 auto"
            contentMaxWidth={contentMaxWidth}>
            {mainContent}
          </MainContent>
          {sidebarContent && (
            <SidebarContent maxSidebarWidth={maxSidebarWidth} transparentSidebar={transparentSidebar} flex="1 1 auto">
              {sidebarContent}
            </SidebarContent>
          )}
        </ContentChildren>
      </Content>
      {secondaryContent}
    </DynamicThemeProvider>
  </>
);

PageContent.propTypes = {
  pageHeader: PropTypes.node,
  pageHeaderAside: PropTypes.node,
  pageHeaderSecondary: PropTypes.node,
  mainContent: PropTypes.node,
  secondaryContent: PropTypes.node,
  sidebarContent: PropTypes.node,
  fullWidth: PropTypes.bool,
  promoteSidebar: PropTypes.bool,
  transparentSidebar: PropTypes.bool,
  maxSidebarWidth: PropTypes.string,
  contentMaxWidth: PropTypes.string,
  isPageLoading: PropTypes.bool,
  pageLoadingDuration: PropTypes.number,
};

export default PageContent;
